import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiFehler, hole, schicke } from '../lib/api'
import { datumHeute, datumKurz, tageAddieren, wochenFenster } from '../lib/datum'
import {
  SLOTS,
  SLOT_NAME,
  proTagUndSlot,
  uebernahmeText,
  type PlanEintrag,
  type Slot,
} from '../lib/mahlzeiten'
import type { EinkaufsListe, Rezept, Zutat } from '../lib/modelle'
import { useDarf, useSitzung } from '../session/SessionProvider'
import { useFab } from '../shell/FabSlot'
import {
  Button,
  Card,
  Chip,
  Empty,
  IconButton,
  ListItem,
  SegmentedButtons,
  Select,
  Sheet,
  TextField,
  useSnackbar,
} from '../ui'

type Ansicht = 'woche' | 'rezepte'

interface RezeptForm {
  id: string | null
  title: string
  servings: number
  notes: string
  ingredients: Zutat[]
}

const LEERES_REZEPT: RezeptForm = {
  id: null,
  title: '',
  servings: 2,
  notes: '',
  ingredients: [{ name: '', quantity: null }],
}

export function Mahlzeiten() {
  const qc = useQueryClient()
  const { abfrage } = useSitzung()
  const preview = Boolean(abfrage.data && !abfrage.data.setupRequired && abfrage.data.preview)
  const darfAnlegen = useDarf('meals.create') && !preview
  const darfAendern = useDarf('meals.edit') && !preview
  const darfArchivieren = useDarf('meals.delete') && !preview
  const darfEinkaufen = useDarf('shopping.create') && useDarf('shopping.view') && !preview

  const [ansicht, setAnsicht] = useState<Ansicht>('woche')
  const [anker, setAnker] = useState(datumHeute())
  const [fehler, setFehler] = useState<string | null>(null)
  const melde = useSnackbar()

  const woche = wochenFenster(anker)
  const planKey = ['meals', 'plan', woche.von, woche.bis] as const

  const rezepte = useQuery({
    queryKey: ['meals', 'recipes'],
    queryFn: () => hole<{ recipes: Rezept[] }>('/api/meals/recipes'),
  })
  const plan = useQuery({
    queryKey: planKey,
    queryFn: () =>
      hole<{ entries: PlanEintrag[] }>(`/api/meals/plan?von=${woche.von}&bis=${woche.bis}`),
  })
  const listen = useQuery({
    queryKey: ['shopping'],
    queryFn: () => hole<{ lists: EinkaufsListe[] }>('/api/shopping'),
    enabled: darfEinkaufen,
  })

  const meldeFehler = (e: unknown, sonst: string) =>
    setFehler(e instanceof ApiFehler ? e.message : sonst)
  const frisch = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ['meals'] }),
      qc.invalidateQueries({ queryKey: ['dashboard'] }),
      qc.invalidateQueries({ queryKey: ['activity'] }),
    ])

  // ── Rezepte ────────────────────────────────────────────────────────────
  const [form, setForm] = useState<RezeptForm | null>(null)
  const [suche, setSuche] = useState('')

  const speichern = useMutation({
    mutationFn: (f: RezeptForm) => {
      const body = {
        title: f.title.trim(),
        servings: f.servings,
        notes: f.notes.trim() || null,
        ingredients: f.ingredients
          .filter((z) => z.name.trim().length > 0)
          .map((z) => ({ name: z.name.trim(), quantity: z.quantity?.trim() || null })),
      }
      return f.id
        ? schicke('PATCH', `/api/meals/recipes/${f.id}`, body)
        : schicke('POST', '/api/meals/recipes', body)
    },
    onSuccess: async () => {
      setForm(null)
      setFehler(null)
      await frisch()
    },
    onError: (e: unknown) => meldeFehler(e, 'Das Rezept konnte nicht gespeichert werden.'),
  })

  const archivieren = useMutation({
    mutationFn: (id: string) => schicke('DELETE', `/api/meals/recipes/${id}`),
    onSuccess: async () => {
      setFehler(null)
      await frisch()
    },
    onError: (e: unknown) => meldeFehler(e, 'Das Rezept konnte nicht archiviert werden.'),
  })

  const rezeptAufListe = useMutation({
    mutationFn: ({ rezeptId, listId }: { rezeptId: string; listId: string }) =>
      schicke<{ added: number }>('POST', `/api/meals/recipes/${rezeptId}/to-shopping-list`, {
        listId,
      }),
    onSuccess: async (r) => {
      setFehler(null)
      melde(uebernahmeText(r.added))
      await qc.invalidateQueries({ queryKey: ['shopping'] })
    },
    onError: (e: unknown) => meldeFehler(e, 'Die Zutaten konnten nicht übernommen werden.'),
  })

  // ── Wochenplan ─────────────────────────────────────────────────────────
  const [neu, setNeu] = useState<{
    onDate: string
    slot: Slot
    recipeId: string
    customTitle: string
  } | null>(null)

  const einplanen = useMutation({
    mutationFn: (n: NonNullable<typeof neu>) =>
      schicke('POST', '/api/meals/plan', {
        onDate: n.onDate,
        slot: n.slot,
        ...(n.recipeId ? { recipeId: n.recipeId } : { customTitle: n.customTitle.trim() }),
      }),
    onSuccess: async () => {
      setNeu(null)
      setFehler(null)
      await frisch()
    },
    onError: (e: unknown) => meldeFehler(e, 'Der Eintrag konnte nicht angelegt werden.'),
  })

  const austragen = useMutation({
    mutationFn: (id: string) => schicke('DELETE', `/api/meals/plan/${id}`),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: planKey })
      const vorher = qc.getQueryData<{ entries: PlanEintrag[] }>(planKey)
      qc.setQueryData<{ entries: PlanEintrag[] }>(planKey, (alt) =>
        alt ? { entries: alt.entries.filter((e) => e.id !== id) } : alt,
      )
      return { vorher }
    },
    onError: (e: unknown, _id, k) => {
      qc.setQueryData(planKey, k?.vorher)
      meldeFehler(e, 'Der Eintrag konnte nicht entfernt werden.')
    },
    onSettled: () => frisch(),
  })

  const wocheAufListe = useMutation({
    mutationFn: (listId: string) =>
      schicke<{ added: number; recipes: number }>('POST', '/api/meals/plan/to-shopping-list', {
        listId,
        von: woche.von,
        bis: woche.bis,
      }),
    onSuccess: async (r) => {
      setFehler(null)
      melde(uebernahmeText(r.added, r.recipes))
      await qc.invalidateQueries({ queryKey: ['shopping'] })
    },
    onError: (e: unknown) => meldeFehler(e, 'Die Woche konnte nicht übernommen werden.'),
  })

  const alleRezepte = rezepte.data?.recipes ?? []
  const nadel = suche.trim().toLocaleLowerCase('de-DE')
  const gefiltert = nadel
    ? alleRezepte.filter((r) => r.title.toLocaleLowerCase('de-DE').includes(nadel))
    : alleRezepte
  const raster = proTagUndSlot(plan.data?.entries ?? [])
  const tage = Array.from({ length: 7 }, (_, i) => tageAddieren(woche.von, i))
  const heute = datumHeute()
  const listenAuswahl = listen.data?.lists ?? []

  const planVorgabe = () => ({
    onDate: tage.includes(heute) ? heute : woche.von,
    slot: 'dinner' as Slot,
    recipeId: '',
    customTitle: '',
  })

  // Die Haupthandlung hängt an der Ansicht: in der Woche plant man, bei den
  // Rezepten legt man Rezepte an. Zwei Knöpfe nebeneinander wären zwei
  // Angebote, von denen eines gerade nicht zur Seite passt.
  useFab(
    darfAnlegen
      ? ansicht === 'woche'
        ? {
            label: 'Mahlzeit eintragen',
            icon: 'plus',
            onClick: () => {
              setNeu(planVorgabe())
            },
          }
        : {
            label: 'Rezept anlegen',
            icon: 'plus',
            onClick: () => {
              setForm(LEERES_REZEPT)
            },
          }
      : null,
  )

  if (rezepte.isError || plan.isError)
    return (
      <p className="fehler" role="alert">
        Die Daten konnten nicht geladen werden.{' '}
        <Button
          variant="text"
          onClick={() => {
            void rezepte.refetch()
            void plan.refetch()
          }}
        >
          Erneut versuchen
        </Button>
      </p>
    )

  return (
    <>
      <div className="seitenkopf">
        <div>
          <h1>Mahlzeiten</h1>
          <p>Was diese Woche auf den Tisch kommt — und die Rezepte dahinter.</p>
        </div>
      </div>

      <SegmentedButtons
        label="Ansicht"
        wert={ansicht}
        optionen={[
          { wert: 'woche', label: 'Woche' },
          { wert: 'rezepte', label: 'Rezepte' },
        ]}
        onWert={setAnsicht}
      />

      {fehler ? (
        <p className="fehler" role="alert">
          {fehler}
        </p>
      ) : null}

      {ansicht === 'woche' ? (
        <>
          <Card titel={`${datumKurz(woche.von)} – ${datumKurz(woche.bis)}`}>
            <div className="reihe reihe--mitte">
              <IconButton
                name="chevron-left"
                label="Vorige Woche"
                onClick={() => {
                  setAnker(tageAddieren(anker, -7))
                }}
              />
              <Button
                variant="text"
                onClick={() => {
                  setAnker(heute)
                }}
              >
                Diese Woche
              </Button>
              <IconButton
                name="chevron-right"
                label="Nächste Woche"
                onClick={() => {
                  setAnker(tageAddieren(anker, 7))
                }}
              />
            </div>
            {darfEinkaufen ? (
              <ListenWahl
                label="Woche auf die Einkaufsliste"
                listen={listenAuswahl}
                laeuft={wocheAufListe.isPending}
                onWahl={(listId) => {
                  wocheAufListe.mutate(listId)
                }}
              />
            ) : null}
          </Card>

          <Sheet
            offen={neu !== null}
            titel="Mahlzeit eintragen"
            onSchliessen={() => {
              setNeu(null)
            }}
          >
            {neu ? (
              <form
                className="stack-4"
                onSubmit={(e) => {
                  e.preventDefault()
                  einplanen.mutate(neu)
                }}
              >
                <div className="reihe reihe--felder">
                  <Select
                    id="plan-tag"
                    label="Tag"
                    wert={neu.onDate}
                    onWert={(w) => {
                      setNeu({ ...neu, onDate: w })
                    }}
                  >
                    {tage.map((t) => (
                      <option key={t} value={t}>
                        {datumKurz(t)}
                      </option>
                    ))}
                  </Select>
                  <Select
                    id="plan-slot"
                    label="Mahlzeit"
                    wert={neu.slot}
                    onWert={(w) => {
                      setNeu({ ...neu, slot: w as Slot })
                    }}
                  >
                    {SLOTS.map((s) => (
                      <option key={s} value={s}>
                        {SLOT_NAME[s]}
                      </option>
                    ))}
                  </Select>
                </div>
                <Select
                  id="plan-rezept"
                  label="Rezept"
                  wert={neu.recipeId}
                  onWert={(w) => {
                    setNeu({ ...neu, recipeId: w })
                  }}
                >
                  <option value="">— eigener Titel —</option>
                  {alleRezepte.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.title}
                    </option>
                  ))}
                </Select>
                {!neu.recipeId ? (
                  <TextField
                    id="plan-titel"
                    label="Was gibt es?"
                    wert={neu.customTitle}
                    onWert={(w) => {
                      setNeu({ ...neu, customTitle: w })
                    }}
                    hilfe="Reste, Essen gehen, Oma kocht …"
                    required
                    autoFocus
                  />
                ) : null}
                <div className="blatt__fuss-inline">
                  <Button
                    variant="text"
                    onClick={() => {
                      setNeu(null)
                    }}
                  >
                    Abbrechen
                  </Button>
                  <Button
                    type="submit"
                    laeuft={einplanen.isPending}
                    disabled={!neu.recipeId && !neu.customTitle.trim()}
                  >
                    Eintragen
                  </Button>
                </div>
              </form>
            ) : null}
          </Sheet>

          {plan.isPending ? <p className="unterzeile">Wird geladen …</p> : null}

          {tage.map((tag) => {
            const proSlot = raster.get(tag)
            return (
              <Card key={tag} titel={`${datumKurz(tag)}${tag === heute ? ' · heute' : ''}`}>
                {!proSlot ? (
                  <p className="unterzeile">Nichts geplant.</p>
                ) : (
                  <ul className="liste liste--gruppe">
                    {SLOTS.flatMap((s) =>
                      (proSlot.get(s) ?? []).map((e) => (
                        <ListItem
                          key={e.id}
                          titel={e.title}
                          unterzeile={
                            e.servings
                              ? `${e.recipeId ? 'Rezept · ' : ''}${e.servings} Portionen`
                              : e.recipeId
                                ? 'Rezept'
                                : undefined
                          }
                          fuehrend={<Chip>{SLOT_NAME[s]}</Chip>}
                          folgend={
                            darfAendern ? (
                              <Button
                                variant="text"
                                laeuft={austragen.isPending}
                                onClick={() => {
                                  austragen.mutate(e.id)
                                }}
                              >
                                Austragen
                              </Button>
                            ) : undefined
                          }
                        />
                      )),
                    )}
                  </ul>
                )}
              </Card>
            )
          })}
        </>
      ) : (
        <>
          <Card titel="Rezepte">
            <TextField
              id="rezept-suche"
              label="Suchen"
              type="search"
              wert={suche}
              onWert={setSuche}
              hilfe="Lasagne …"
            />
          </Card>

          <Sheet
            offen={form !== null}
            titel={form?.id ? 'Rezept bearbeiten' : 'Neues Rezept'}
            onSchliessen={() => {
              setForm(null)
            }}
          >
            {form ? (
              <RezeptEditor
                form={form}
                laeuft={speichern.isPending}
                onChange={setForm}
                onAbbruch={() => {
                  setForm(null)
                }}
                onSpeichern={() => {
                  speichern.mutate(form)
                }}
              />
            ) : null}
          </Sheet>

          {rezepte.isPending ? <p className="unterzeile">Wird geladen …</p> : null}

          {gefiltert.length === 0 ? (
            <Empty
              titel={nadel ? 'Kein Rezept passt' : 'Noch kein Rezept'}
              text={
                nadel
                  ? 'Ein anderer Suchbegriff bringt vielleicht mehr.'
                  : darfAnlegen
                    ? 'Der Knopf unten rechts legt das erste an — Zutaten kann man später nachtragen.'
                    : 'Hier steht später, was gekocht wird.'
              }
            />
          ) : (
            <Card titel={nadel ? `Treffer für „${suche.trim()}"` : 'Alle Rezepte'}>
              <ul className="liste liste--gruppe">
                {gefiltert.map((r) => (
                  // ⚠️ Die Handlungen stehen UNTER der Zeile, nicht in deren
                  // Nachlauf. Drei Knöpfe dort nehmen auf einem Telefon die
                  // ganze Breite, und der Titel bricht dann Buchstabe für
                  // Buchstabe um - im Bild nachgewiesen. Dieselbe Anordnung
                  // wie bei Aufgaben und Terminen.
                  <li className="rezept-eintrag" key={r.id}>
                    <ListItem
                      as="div"
                      titel={r.title}
                      unterzeile={`${r.servings} Portionen · ${
                        r.ingredients.length === 0
                          ? 'noch keine Zutaten'
                          : r.ingredients.map((z) => z.name).join(', ')
                      }`}
                    />
                    <div className="reihe rezept-aktionen">
                      {darfAendern ? (
                        <Button
                          variant="text"
                          onClick={() => {
                            setForm({
                              id: r.id,
                              title: r.title,
                              servings: r.servings,
                              notes: r.notes ?? '',
                              ingredients: r.ingredients.length
                                ? r.ingredients.map((z) => ({
                                    name: z.name,
                                    quantity: z.quantity,
                                  }))
                                : [{ name: '', quantity: null }],
                            })
                          }}
                        >
                          Bearbeiten
                        </Button>
                      ) : null}
                      {darfEinkaufen && r.ingredients.length > 0 ? (
                        <ListenWahl
                          label="Auf die Liste"
                          kompakt
                          listen={listenAuswahl}
                          laeuft={rezeptAufListe.isPending}
                          onWahl={(listId) => {
                            rezeptAufListe.mutate({ rezeptId: r.id, listId })
                          }}
                        />
                      ) : null}
                      {/* Als Zeichen-Knopf: drei ausgeschriebene Handlungen
                          passen auf einem Telefon nicht nebeneinander, die
                          dritte fiel in eine eigene Zeile. Der Name bleibt
                          über die Beschriftung vollständig erhalten. */}
                      {darfArchivieren ? (
                        <IconButton
                          name="trash"
                          label={`${r.title} archivieren`}
                          disabled={archivieren.isPending}
                          onClick={() => {
                            archivieren.mutate(r.id)
                          }}
                        />
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </>
      )}
    </>
  )
}

/**
 * Listenauswahl für die Übernahme. Bei genau EINER Liste braucht es keine
 * Auswahl — ein Knopf genügt. Ohne Liste sagt die Oberfläche das, statt einen
 * toten Knopf zu zeigen.
 */
function ListenWahl({
  label,
  listen,
  laeuft,
  onWahl,
  kompakt = false,
}: {
  label: string
  listen: EinkaufsListe[]
  laeuft: boolean
  onWahl: (listId: string) => void
  kompakt?: boolean
}) {
  const [gewaehlt, setGewaehlt] = useState('')
  if (listen.length === 0)
    return kompakt ? null : (
      <p className="unterzeile">Für die Übernahme braucht es zuerst eine Einkaufsliste.</p>
    )
  const einzige = listen.length === 1 ? listen[0] : undefined
  if (einzige) {
    return (
      <Button
        variant={kompakt ? 'text' : 'tonal'}
        laeuft={laeuft}
        onClick={() => {
          onWahl(einzige.id)
        }}
      >
        {label}
      </Button>
    )
  }
  const ziel = gewaehlt || listen[0]?.id || ''
  return (
    <div className="reihe reihe--mitte">
      <Select
        id={`ziel-liste-${label}`}
        label="Einkaufsliste"
        wert={ziel}
        onWert={setGewaehlt}
        className="listenwahl__feld"
      >
        {listen.map((l) => (
          <option key={l.id} value={l.id}>
            {l.name}
          </option>
        ))}
      </Select>
      <Button
        variant={kompakt ? 'text' : 'tonal'}
        laeuft={laeuft}
        disabled={!ziel}
        onClick={() => {
          onWahl(ziel)
        }}
      >
        {label}
      </Button>
    </div>
  )
}

function RezeptEditor({
  form,
  laeuft,
  onChange,
  onAbbruch,
  onSpeichern,
}: {
  form: RezeptForm
  laeuft: boolean
  onChange: (f: RezeptForm) => void
  onAbbruch: () => void
  onSpeichern: () => void
}) {
  const setZutat = (i: number, teil: Partial<Zutat>) => {
    onChange({
      ...form,
      ingredients: form.ingredients.map((z, k) => (k === i ? { ...z, ...teil } : z)),
    })
  }
  return (
    <form
      className="stack-4"
      onSubmit={(e) => {
        e.preventDefault()
        onSpeichern()
      }}
    >
      {/* ⚠️ Das Feld heißt „Titel", nicht „Neues Rezept": die Überschrift
          steht am Blatt, und zwei Elemente mit demselben Namen sind für
          Sprachausgabe und Automatisierung nicht auseinanderzuhalten. */}
      <TextField
        id="rezept-titel"
        label="Titel"
        wert={form.title}
        onWert={(w) => {
          onChange({ ...form, title: w })
        }}
        required
        autoFocus
      />
      <TextField
        id="rezept-portionen"
        label="Portionen"
        type="number"
        min={1}
        max={99}
        wert={String(form.servings)}
        onWert={(w) => {
          onChange({ ...form, servings: Math.min(99, Math.max(1, Number(w) || 1)) })
        }}
      />

      {/* ⚠️ Ein echtes <fieldset> mit <legend>: die Zutatenzeilen gehören
          zusammen, und nur so sagt eine Sprachausgabe „Zutaten" vor jedem
          Feld an. Eine Überschrift daneben leistet das nicht. */}
      <fieldset className="feldgruppe">
        <legend>Zutaten</legend>
        <div className="stack-3">
          {form.ingredients.map((z, i) => (
            // Der Index IST hier die Identität: die Zeilen haben keinen
            // eigenen Schlüssel, und eine entfernte Zeile verschiebt die
            // folgenden bewusst nach oben.
            <div className="reihe reihe--felder" key={i}>
              <TextField
                id={`zutat-${i}`}
                label={`Zutat ${i + 1}`}
                wert={z.name}
                onWert={(w) => {
                  setZutat(i, { name: w })
                }}
              />
              <TextField
                id={`menge-${i}`}
                label={`Menge ${i + 1}`}
                wert={z.quantity ?? ''}
                maxLength={40}
                hilfe="z. B. 500 g"
                onWert={(w) => {
                  setZutat(i, { quantity: w })
                }}
              />
              <IconButton
                name="trash"
                label={`Zutat ${i + 1} entfernen`}
                onClick={() => {
                  onChange({ ...form, ingredients: form.ingredients.filter((_, k) => k !== i) })
                }}
              />
            </div>
          ))}
        </div>
        <Button
          variant="text"
          icon="plus"
          onClick={() => {
            onChange({ ...form, ingredients: [...form.ingredients, { name: '', quantity: null }] })
          }}
        >
          Zutat hinzufügen
        </Button>
      </fieldset>

      <div className="tf">
        <textarea
          id="rezept-notizen"
          className="tf__eingabe tf__eingabe--mehrzeilig"
          rows={4}
          placeholder=" "
          value={form.notes}
          onChange={(e) => {
            onChange({ ...form, notes: e.target.value })
          }}
        />
        <label className="tf__label" htmlFor="rezept-notizen">
          Zubereitung
        </label>
      </div>

      <div className="blatt__fuss-inline">
        <Button variant="text" onClick={onAbbruch}>
          Abbrechen
        </Button>
        <Button type="submit" laeuft={laeuft} disabled={!form.title.trim()}>
          Speichern
        </Button>
      </div>
    </form>
  )
}
